import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { useLazyQuery } from '@apollo/client';
import { GET_ASSET_VALUE, SEARCH_ASSETS } from '../../../http/apollo-queries';
import { Input } from '../../form';
import RevealButton from '../../Reveal-button/Reveal-button';
import { DatePicker } from '../../form';
import { formatAWSDate, getLogo } from '../../../helpers/functions';
import { useTutorialOverlay } from '../../../hooks/useTutorialOverlay';
import { AssetAdvancedSettings } from '../AssetAdvancedSettings/AssetAdvancedSettings';
import { useTranslation } from 'react-i18next';
import { useUserStrategies } from '../../../context/UserStrategies';
import { TickerElement } from '../TickerElement';
import { useAuth } from '../../../context/Auth';
import { getPrecision, numberFormat } from '../../../helpers/calc';
import {
  AssetPrice,
  ConfigureAssetProps,
  SearchAsset,
  SearchAssetList,
} from './types';
import { CustomNumber } from '../../CustomNumber/CustomNumber';
import { NumberFormatValues } from 'react-number-format';
import { GetAssetValueVars } from '../types';

const tooltipsArray = [
  <span>
    Define your tracking options by setting stop profit and stop loss for each
    investment and receive an alert every time price exceed your settings
  </span>,
];

export const FinancialAssetForm = ({
  isNewStrategy = false,
  action,
  assetCategory,
  reportingCurrency,
  formik,
  strategyId,
  isTooltipIconVisible = false,
}: ConfigureAssetProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { strategies } = useUserStrategies();
  const scrollableDivRef = useRef<HTMLFormElement>(null);

  const [searchAssets, { loading: loadingSearch }] = useLazyQuery<{
    searchAssets: { items: SearchAsset[] };
  }>(SEARCH_ASSETS);

  const [getAssetPrice, { loading: loadingAssetPrice }] = useLazyQuery<
    AssetPrice,
    GetAssetValueVars
  >(GET_ASSET_VALUE, {
    fetchPolicy: 'network-only',
  });

  const [searchAssetsList, setSearchAssetsList] = useState<SearchAssetList[]>(
    []
  );
  const [isMoreRevealed, setIsMoreRevealed] = useState(false);

  const {
    TutorialOverlay,
    tooltips: [InfoTooltip],
  } = useTutorialOverlay({
    isTooltipIconVisible,
    isTutorialShown: false,
    tooltipsArray,
    tooltipClassname: 'absolute left-60 ml-1',
    onCloseTutorial: () => {},
  });

  const debounceSearchAssets = useCallback(
    debounce((term: string) => {
      searchAssets({
        variables: {
          term: term + '*',
          type: assetCategory.key,
        },
      }).then((response) => {
        if (response.data != null) {
          const {
            data: {
              searchAssets: { items },
            },
          } = response;
          const fetchedSearchAssets = items.map(
            ({ id, logo, name, type, exchange = '', symbol, currency }) => {
              const src = getLogo(type, logo);

              return {
                item: name + ' - ' + symbol.toUpperCase(),
                data: {
                  id,
                  type,
                  logo,
                  currency,
                },
                component: (
                  <TickerElement
                    name={name}
                    src={src}
                    symbol={symbol}
                    type={type}
                    exchange={exchange}
                    currency={currency}
                  />
                ),
              };
            }
          );

          setSearchAssetsList(fetchedSearchAssets);
        } else {
          setSearchAssetsList([]);
        }
      });
    }, 500),
    []
  );

  const dropdownStrategies = useMemo(
    () =>
      user!.membership.membershipPlan.features.strategyAssets === -1
        ? strategies
        : strategies.filter(
            ({ strategyAssets: { items } }) =>
              items.length <
              user!.membership.membershipPlan.features.strategyAssets
          ),
    []
  );

  // HANDLERS
  const handlePurchasedAtChange = (date: Date) => {
    formik.setFieldValue('purchasedAt', formatAWSDate(date));
  };

  // EFFECTS
  // Search asset with debounce on input change
  useEffect(() => {
    (strategyId || isNewStrategy) &&
      debounceSearchAssets(formik.values.assetName || '*');
  }, [formik.values.assetName]);

  // Load last asset price on select change
  useEffect(() => {
    if (
      formik.values.assetData?.id &&
      (!formik.values.entryPriceInAssetCurrency || action !== 'EDIT')
    ) {
      const date = formik.values.purchasedAt || formatAWSDate(new Date());

      getAssetPrice({
        variables: {
          assetId: formik.values.assetData.id,
          date,
        },
      }).then(({ data }) => {
        let entryPriceInReportingCurrency = 0;
        let entryPriceInAssetCurrency = 0;
        let entryExchangePrice = 1;

        if (data?.getAssetValue) {
          entryPriceInReportingCurrency =
            data?.getAssetValue?.[reportingCurrency.toLocaleLowerCase()] ?? 0;

          entryPriceInAssetCurrency = data?.getAssetValue?.cur ?? 0;
          entryExchangePrice =
            entryPriceInReportingCurrency / +entryPriceInAssetCurrency;
        }

        formik.setValues({
          ...formik.values,
          entryPriceInReportingCurrency,
          entryPriceInAssetCurrency,
          entryExchangePrice,
        });
      });
    }
  }, [formik.values.assetData?.id, formik.values.purchasedAt]);

  // Update total price on data change
  useEffect(() => {
    let totalInvestmentInAssetCurrency: number | undefined = undefined;
    let entryPriceInReportingCurrency: number | undefined = undefined;
    let totalInvestmentInReportingCurrency: number | undefined = undefined;

    const entryPriceInAssetCurrency = formik.values.entryPriceInAssetCurrency;

    const quantity = formik.values.quantity;

    if (entryPriceInAssetCurrency && quantity) {
      totalInvestmentInAssetCurrency = entryPriceInAssetCurrency * quantity;

      entryPriceInReportingCurrency =
        entryPriceInAssetCurrency * formik.values.entryExchangePrice;

      totalInvestmentInReportingCurrency =
        entryPriceInAssetCurrency * formik.values.entryExchangePrice * quantity;
    }

    formik.setFieldValue(
      'totalInvestmentInAssetCurrency',
      totalInvestmentInAssetCurrency
    );
    formik.setFieldValue(
      'entryPriceInReportingCurrency',
      entryPriceInReportingCurrency
    );
    formik.setFieldValue(
      'totalInvestmentInReportingCurrency',
      totalInvestmentInReportingCurrency
    );
  }, [
    formik.values.entryPriceInAssetCurrency,
    formik.values.quantity,
    formik.values.purchasedAt,
  ]);

  // Set strategy from dropdown if we're adding an asset from market/detail
  useEffect(() => {
    if (action === 'ADD_TO_STRATEGY') {
      formik.setValues({
        ...formik.values,
        strategyId: dropdownStrategies[0].id,
        strategyName: dropdownStrategies[0].name,
      });
    }
  }, []);

  // Reset form data on load
  useEffect(() => {
    if (action === 'CREATE') {
      formik.setValues({
        ...formik.values,
        purchasedAt: undefined,
        entryPriceInReportingCurrency: undefined,
        quantity: undefined,
        entryPriceInAssetCurrency: undefined,
        currentPriceInAssetCurrency: undefined,
        currentPriceInReportingCurrency: undefined,
        entryExchangePrice: 1,
      });
    }
  }, [action]);

  return (
    <>
      <TutorialOverlay />
      <form
        className="flex w-full h-full overflow-scroll"
        autoComplete={'off'}
        ref={scrollableDivRef}
      >
        <div className="flex flex-col w-full px-8 md:px-20">
          <div className={`flex mt-8 w-full gap-0`}>
            {/* ASSET */}
            <Input
              className={`${
                action === 'ADD_TO_STRATEGY'
                  ? 'cursor-not-allowed'
                  : 'cursor-default'
              }`}
              dropdownClassname={'max-h-80'}
              dropdownDataName="assetData"
              dropdownItems={searchAssetsList.map(({ item }) => item)}
              dropdownItemsComponents={searchAssetsList.map(
                ({ component }) => component
              )}
              dropdownItemsData={searchAssetsList.map(({ data }) => data)}
              formik={formik}
              id="assetName"
              isDisabled={action === 'ADD_TO_STRATEGY' || action === 'EDIT'}
              label={t('strategies.configureAsset.form.label.search')}
              loading={loadingSearch}
              key="assetName"
              name="assetName"
              placeholder={t(
                'strategies.configureAsset.form.placeholder.search'
              )}
              size="full"
              type={action === 'ADD_TO_STRATEGY' ? 'text' : 'searchDropdown'}
              validate={(value: string) => {
                return value.length === 0;
              }}
            />
          </div>

          {action === 'ADD_TO_STRATEGY' && (
            <div className={`flex mt-16 w-full gap-0`}>
              {/* STRATEGY */}
              <Input
                dropdownDataName="strategyId"
                dropdownItems={dropdownStrategies
                  .filter((s) => s.status === 'COMPLETED')
                  .map(({ name }) => name)}
                dropdownItemsData={dropdownStrategies
                  .filter((s) => s.status === 'COMPLETED')
                  .map(({ id }) => id)}
                formik={formik}
                id={'strategyName'}
                label={t('strategies.configureAsset.form.label.strategy')}
                name={'strategyName'}
                placeholder={t(
                  'strategies.configureAsset.form.placeholder.strategy'
                )}
                size={'full'}
                type={'dropdown'}
              />
            </div>
          )}

          <div className="flex mt-16 gap-8">
            {/* PRICE */}
            <div className={'w-full'}>
              <CustomNumber
                allowNegative={false}
                decimalScale={getPrecision(
                  formik.values.entryPriceInAssetCurrency || 0
                )}
                decimalScaleOnFocus={getPrecision(
                  formik.values.entryPriceInAssetCurrency || 0
                )}
                fixedLabel={false}
                id={'entryPriceInAssetCurrency'}
                label={t(
                  'strategies.configureAsset.form.placeholder.entryPriceInAssetCurrency'
                )}
                loading={loadingAssetPrice}
                onValueChange={({ floatValue }: NumberFormatValues) =>
                  formik.setFieldValue('entryPriceInAssetCurrency', floatValue)
                }
                placeholder={t(
                  'strategies.configureAsset.form.label.entryPriceInAssetCurrency'
                )}
                prefix={undefined}
                readOnly={false}
                showLabel={true}
                suffix={formik.values.assetData?.currency || reportingCurrency}
                suffixOverlay={true}
                value={formik.values.entryPriceInAssetCurrency}
                validate={(value) => !!value && value > 0}
              />

              {formik.values.entryPriceInReportingCurrency !== undefined && (
                <div className={'text-xs text-grey-50'}>
                  {t('strategies.configureAsset.inReportingCurrency', {
                    value: numberFormat(
                      formik.values.entryPriceInReportingCurrency
                    ),
                    currency: reportingCurrency,
                  })}
                </div>
              )}
            </div>

            {/* QTY */}
            <div className={'w-full'}>
              <CustomNumber
                allowNegative={false}
                decimalScale={getPrecision(formik.values.quantity || 0)}
                decimalScaleOnFocus={getPrecision(formik.values.quantity || 0)}
                fixedLabel={false}
                id={'quantity'}
                label={t('strategies.configureAsset.form.placeholder.quantity')}
                loading={loadingAssetPrice}
                onValueChange={({ floatValue }: NumberFormatValues) =>
                  formik.setFieldValue('quantity', floatValue)
                }
                placeholder={t('strategies.configureAsset.form.label.quantity')}
                readOnly={false}
                showLabel={true}
                suffixOverlay={true}
                validate={(value) => !!value && value > 0}
                value={formik.values.quantity}
              />
            </div>
          </div>

          <div className="flex mt-16 gap-8">
            {/* TOTAL */}
            <div className={'w-full'}>
              <CustomNumber
                allowNegative={false}
                fixedLabel={false}
                id={'totalInvestmentInAssetCurrency'}
                isDisabled={true}
                label={t(
                  'strategies.configureAsset.form.placeholder.totalInvestmentInAssetCurrency'
                )}
                loading={loadingAssetPrice}
                onValueChange={({ floatValue }: NumberFormatValues) =>
                  formik.setFieldValue(
                    'totalInvestmentInAssetCurrency',
                    floatValue
                  )
                }
                placeholder={t(
                  'strategies.configureAsset.form.label.totalInvestmentInAssetCurrency'
                )}
                readOnly={true}
                showLabel={true}
                suffix={formik.values.assetData?.currency || reportingCurrency}
                suffixOverlay={true}
                value={formik.values.totalInvestmentInAssetCurrency}
                validate={(value) => !!value && value > 0}
              />

              {formik.values.totalInvestmentInReportingCurrency && (
                <div className={'text-xs text-grey-30'}>
                  {t('strategies.configureAsset.inReportingCurrency', {
                    value: numberFormat(
                      formik.values.totalInvestmentInReportingCurrency
                    ),
                    currency: reportingCurrency,
                  })}
                </div>
              )}
            </div>

            {/* DATE */}
            <div className={'w-full'}>
              <DatePicker
                label={t('strategies.configureAsset.form.label.purchasedAt')}
                size="full"
                placeholder={t(
                  'strategies.configureAsset.form.placeholder.purchasedAt'
                )}
                date={
                  formik.values.purchasedAt
                    ? new Date(formik.values.purchasedAt)
                    : new Date()
                }
                id={'date'}
                maxDate={new Date()}
                showLabel={true}
                fixedLabel={false}
                onChange={(date) => handlePurchasedAtChange(date as Date)}
                validate={(value) => !!value}
              />
            </div>
          </div>

          {/* ADVANCED SETTINGS */}
          <div className="relative flex flex-col mt-16">
            <RevealButton
              className="mb-5 text-xs"
              size="large"
              revealedLabel={t('strategies.configureAsset.advancedSettings')}
              notRevealedLabel={t('strategies.configureAsset.advancedSettings')}
              setIsMoreRevealed={setIsMoreRevealed}
              onToggle={() => {
                if (scrollableDivRef && scrollableDivRef.current) {
                  scrollableDivRef.current.scrollTop += 300;
                }
              }}
            />
            {isTooltipIconVisible && (
              <InfoTooltip
                tooltipClassname={'-mt-12 ml-40 pt-1 w-8 inline-block'}
              />
            )}
            {isMoreRevealed && (
              <div className="flex flex-col">
                <AssetAdvancedSettings
                  formik={formik}
                  reportingCurrency={reportingCurrency}
                  assetCurrency={
                    formik.values.assetData?.currency || reportingCurrency
                  }
                  entryExchangePrice={formik.values.entryExchangePrice}
                />
              </div>
            )}
          </div>
        </div>
      </form>
    </>
  );
};
